/**
 * <TutorLiveStudentPanel> — la fiche d'un élève, ouverte pendant le live (organism).
 *
 * « Son profil » sur sa carte : la même fiche qu'hors live (`<TutorStudentProfilePage>`), sans
 * « Messages » (le live a son chat) et avec « Pour ce live », dans le contenant que la salle offre :
 * la colonne du fil, ou son tiroir (`<TutorLivePage>`). En tête, le retour et ce qu'il rend (« Le fil »,
 * avec le compte de ce qui y attend le masteur ; « Ses activités » au téléphone) et la croix.
 */
import React from "react"
import { Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Badge } from "../../atoms/Badge"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { Row } from "../../atoms/containers/Row"
import { Icon } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"
import { TutorStudentProfilePage, type TutorStudentProfilePageProps } from "../../pages/TutorStudentProfilePage"
import { cappedCount } from "../../utils/cappedCount"

export type TutorLiveStudentPanelProps = {
    /** La fiche, sans ce qui n'existe pas en live : le retour, « Activité », Messages. */
    fiche: Omit<TutorStudentProfilePageProps, "live" | "top" | "onBack" | "backLabel" | "onGenerate" | "messages">
    /** « Le fil », « Ses activités ». */
    backLabel: string
    /** Ce qui attend le masteur derrière ce retour. 0 : rien. */
    backCount?: number
    onBack: () => void
    onClose: () => void
    testID?: string
}

export function TutorLiveStudentPanel(props: TutorLiveStudentPanelProps) {
    const { fiche, backLabel, backCount = 0, onBack, onClose, testID } = props
    return (
        <TutorStudentProfilePage
            {...fiche}
            live
            // Une fiche étalée n'est pas vérifiée prop à prop : ce qui n'existe pas en live reste éteint.
            onBack={undefined}
            onGenerate={undefined}
            messages={undefined}
            testID={testID}
            top={
                <Row justify="between" align="center">
                    <Pressable
                        onPress={onBack}
                        accessibilityRole="button"
                        accessibilityLabel={`Revenir à : ${backLabel}`}
                        style={styles.back}
                    >
                        <Icon name="chevron-left" size={16} color="primary" />
                        <Text size="text-sm" weight="bold">
                            {backLabel}
                        </Text>
                        {backCount > 0 && (
                            <View>
                                <Badge text={cappedCount(backCount)} variant="danger" />
                            </View>
                        )}
                    </Pressable>
                    <IconButton
                        iconName="times"
                        appearance="transparent"
                        iconColor="primary"
                        size={40}
                        onPress={onClose}
                        accessibilityLabel="Fermer sa fiche"
                    />
                </Row>
            }
        />
    )
}

const styles = StyleSheet.create((theme) => ({
    back: {
        flexDirection: "row",
        alignItems: "center",
        gap: theme.spacing["1"],
        minHeight: 36,
        paddingLeft: theme.spacing["2"],
        paddingRight: theme.spacing["3"],
        borderRadius: theme.radius.full,
        backgroundColor: theme.colors["surface-subtle"],
    },
}))
